import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { employerRequestAPI } from '../services/api';
import toast from 'react-hot-toast';
import { formatAxiosError } from '../utils/apiError';

const RegisterEmployer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    
    // Dismiss any existing toasts
    toast.dismiss();
    
    try {
      const requestData = {
        companyName: data.companyName,
        contactFirstName: data.contactFirstName,
        contactLastName: data.contactLastName,
        email: data.email,
        phone: data.phone ? data.phone.replace(/\D/g, '') : undefined,
        address: data.address,
        message: data.message || undefined
      };

      const response = await employerRequestAPI.create(requestData);
      
      if (response.data.success) {
        toast.success('Request submitted successfully! Our team will contact you shortly.', {
          duration: 5000
        });
        
        // Delay navigation to show the toast
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting employer request:', error);
      const errorMessage = formatAxiosError(error, 'Failed to submit request. Please try again.');
      toast.error(errorMessage, {
        duration: 5000
      });
      setIsLoading(false);
    }
  };

  const validatePhone = (value) => {
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = value.replace(/\D/g, '');
    return phoneRegex.test(cleanPhone) || 'Please enter a valid phone number';
  };

  const validateEmail = (value) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(value) || 'Please enter a valid email address';
  };

  return (
    <div className="auth-page">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <img
            src={`${process.env.PUBLIC_URL}/liaizn-logo.png`}
            alt="Liaizn"
            className="mx-auto h-14 w-auto mb-3 object-contain"
          />
          <h2 className="text-2xl font-bold text-secondary-900">Request Employer Access</h2>
          <p className="mt-2 text-sm text-secondary-600">
            Partner with us to find verified talent for your organization
          </p>
          <p className="mt-2 text-sm text-secondary-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="auth-card">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="companyName" className="form-label">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                id="companyName"
                type="text"
                placeholder="Your Company Name"
                className={`input-field ${errors.companyName ? 'border-red-500' : ''}`}
                {...register('companyName', {
                  required: 'Company name is required',
                  minLength: {
                    value: 2,
                    message: 'Company name must be at least 2 characters'
                  }
                })}
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="contactFirstName" className="form-label">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="contactFirstName"
                  type="text"
                  autoComplete="given-name"
                  className={`input-field ${errors.contactFirstName ? 'border-red-500' : ''}`}
                  {...register('contactFirstName', {
                    required: 'First name is required',
                    minLength: {
                      value: 1,
                      message: 'First name must be at least 1 character'
                    }
                  })}
                />
                {errors.contactFirstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactFirstName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="contactLastName" className="form-label">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="contactLastName"
                  type="text"
                  autoComplete="family-name"
                  className={`input-field ${errors.contactLastName ? 'border-red-500' : ''}`}
                  {...register('contactLastName', {
                    required: 'Last name is required',
                    minLength: {
                      value: 1,
                      message: 'Last name must be at least 1 character'
                    }
                  })}
                />
                {errors.contactLastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.contactLastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="form-label">
                Organization Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="contact@company.com"
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  validate: validateEmail
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="address" className="form-label">
                Company Address <span className="text-red-500">*</span>
              </label>
              <textarea
                id="address"
                rows="3"
                placeholder="Enter your company address"
                className={`input-field ${errors.address ? 'border-red-500' : ''}`}
                {...register('address', {
                  required: 'Address is required',
                  minLength: {
                    value: 10,
                    message: 'Please provide a complete address'
                  }
                })}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="message" className="form-label">
                Additional Information (Optional)
              </label>
              <textarea
                id="message"
                rows="4"
                placeholder="Tell us about your hiring needs, company size, or any specific requirements..."
                className="input-field"
                {...register('message')}
              />
            </div>

            <div className="flex items-center">
              <input
                id="agree-contact"
                name="agree-contact"
                type="checkbox"
                className="h-4 w-4 text-accent-600 focus:ring-accent-500 border-gray-300 rounded"
                {...register('agreeContact', {
                  required: 'You must agree to be contacted'
                })}
              />
              <label htmlFor="agree-contact" className="ml-2 block text-sm text-gray-900">
                I agree to be contacted by the Liaizn team regarding this request
              </label>
            </div>
            {errors.agreeContact && (
              <p className="text-sm text-red-600">{errors.agreeContact.message}</p>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Submitting Request...' : 'Request Access'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-8 text-center pb-8">
        <p className="text-sm text-gray-500">
          Our team will review your request and get back to you within 24-48 hours
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Looking to learn?{' '}
          <Link to="/register/candidate" className="text-primary-600 hover:text-primary-700 font-medium">
            Register as a Candidate
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterEmployer;

